/**
 * An example motocross line laid on the real terrain: rollers, two
 * tabletops, a double and a big one, shaped like the jumps on a pro track —
 * a kicker that steepens to its lip, and a long downslope to land on.
 *
 * A line is a heightfield on the ground: a trapezoid in section, packed dirt
 * on top and banked sides, draped over whatever terrain it was laid on. It
 * joins the ground the game samples (`heightAt`), so the bike, the car and
 * anyone on foot ride and walk on it, and fly off its lips by the same rule
 * as off any crest. It is anchored by latitude and longitude, so it stays put
 * however the world's local frame moves.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
} from 'three';
import { DEG_TO_RAD, EARTH_RADIUS } from '../core/geo/mercator.js';

const METRES_PER_DEG = DEG_TO_RAD * EARTH_RADIUS;

/** The riding surface's width, metres, and how steeply the sides fall away (rise per run). */
export const TRACK_WIDTH = 7;
const SIDE_SLOPE = 0.8;
/** The packed track stands this proud of the ground between jumps. */
const TRACK_LIFT = 0.1;
/** Grid spacing, metres: fine along the line, so a lip stays sharp. */
const STEP_U = 0.5;
const STEP_V = 0.5;

type Point = [number, number];

/** A kicker: from the ground, steepening to its lip, `height` up over `length`. */
function kicker(at: number, height: number, length: number): Point[] {
  const out: Point[] = [];
  for (let i = 0; i <= 8; i++) {
    const t = i / 8;
    out.push([at + t * length, height * t ** 1.6]);
  }
  return out;
}

/** A landing: from `height` down to the ground over `length`, rounding out at the bottom. */
function landing(at: number, height: number, length: number): Point[] {
  const out: Point[] = [];
  for (let i = 0; i <= 6; i++) {
    const t = i / 6;
    // Straight for most of it, easing out at the foot.
    out.push([at + t * length, height * (1 - t) ** 1.25]);
  }
  return out;
}

/** The line, as (distance along, height above the ground) control points. */
function buildProfile(): Point[] {
  const p: Point[] = [[0, 0], [12, 0]];
  // Three rollers, to pump.
  for (let i = 0; i <= 36; i++) {
    const u = 12 + i * 0.5;
    p.push([u, 0.7 * Math.sin((Math.PI * (u - 12)) / 6) ** 2]);
  }
  // A small tabletop.
  p.push(...kicker(38, 1.4, 3.2), [49.2, 1.4], ...landing(49.2, 1.4, 8));
  // A bigger one.
  p.push([70, 0], ...kicker(70, 2.2, 4.6), [86.6, 2.2], ...landing(86.6, 2.2, 11));
  // A double: take off, clear the gap, land on the back of the second.
  p.push([110, 0], ...kicker(110, 2, 4.2), [114.4, 0], [127.2, 0], [130, 2], ...landing(130, 2, 12));
  // The big one: a tall kicker and a sixteen-metre gap to a long landing.
  p.push([156, 0], ...kicker(156, 3, 6.5), [162.8, 0], [178.5, 0], [182, 3], ...landing(182, 3, 18), [212, 0]);
  return p;
}

const PROFILE = buildProfile();
export const TRACK_LENGTH = PROFILE[PROFILE.length - 1][0];
/** Where each jump's lip is, metres along: flags mark them. */
const LIPS = [41.2, 74.6, 114.2, 162.5];

/** Height of the line above the ground at `u` metres along its middle. */
export function trackProfile(u: number): number {
  if (u <= 0 || u >= TRACK_LENGTH) return 0;
  // Control points are in order; find the pair either side.
  let lo = 0, hi = PROFILE.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (PROFILE[mid][0] <= u) lo = mid;
    else hi = mid;
  }
  const [u0, h0] = PROFILE[lo], [u1, h1] = PROFILE[hi];
  return u1 === u0 ? Math.max(h0, h1) : h0 + ((h1 - h0) * (u - u0)) / (u1 - u0);
}

/** Height above the ground at `u` along and `v` across: the section's trapezoid, and the packed track. */
export function trackHeight(u: number, v: number): number {
  const edge = Math.abs(v) - TRACK_WIDTH / 2;
  const jump = Math.max(0, trackProfile(u) - SIDE_SLOPE * Math.max(0, edge));
  const lift = u > 0 && u < TRACK_LENGTH && edge < 0.4 ? TRACK_LIFT : 0;
  return Math.max(jump, lift);
}

/** How far the footprint reaches either side of the middle. */
const HALF = TRACK_WIDTH / 2 + 3.2 / SIDE_SLOPE + STEP_V;

const dirt = new MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.95 });
const pole = new MeshStandardMaterial({ color: new Color(0xf3f0e8), roughness: 0.6, flatShading: true });
const flag = new MeshStandardMaterial({ color: new Color(0xf0772f), roughness: 0.6, flatShading: true });
const TOP = new Color(0x9c754a), SIDE = new Color(0x7b5836), GRASS_EDGE = new Color(0x6f6a38);

/** Ground under a coordinate, or undefined while it is still streaming in. */
export type Ground = (lat: number, lon: number) => number | undefined;

export class JumpLine {
  readonly group = new Group();
  private readonly cols = Math.round((2 * HALF) / STEP_V) + 1;
  private readonly rows = Math.round(TRACK_LENGTH / STEP_U) + 1;
  /** Absolute height of the surface at each node, row by row. */
  private top: Float32Array | undefined;
  private readonly fe: number;
  private readonly fn: number;

  /**
   * A line starting at (lat, lon) and running along `yaw` (in the sense of
   * `Object3D.rotation.y`: forward is (-sin, -cos) in scene x and z).
   */
  constructor(readonly lat: number, readonly lon: number, yaw: number) {
    this.fe = -Math.sin(yaw);
    this.fn = Math.cos(yaw);
  }

  get built(): boolean {
    return this.top !== undefined;
  }

  /** Metres east and north of the anchor at `u` along and `v` right. Right of (e, n) is (n, -e). */
  private offset(u: number, v: number): [number, number] {
    return [this.fe * u + this.fn * v, this.fn * u - this.fe * v];
  }

  /**
   * Lay the line on the ground. Returns false, and builds nothing, while any
   * of the ground under it has not streamed in yet: try again next frame.
   */
  build(ground: Ground): boolean {
    if (this.top !== undefined) return true;
    const { rows, cols } = this;
    const cosLat = Math.max(1e-6, Math.cos(this.lat * DEG_TO_RAD));
    const base = new Float32Array(rows * cols);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const [e, n] = this.offset(r * STEP_U, -HALF + c * STEP_V);
        const g = ground(this.lat + n / METRES_PER_DEG, this.lon + e / (METRES_PER_DEG * cosLat));
        if (g === undefined) return false;
        base[r * cols + c] = g;
      }
    }
    const top = new Float32Array(rows * cols);
    const position = new Float32Array(rows * cols * 3);
    const color = new Float32Array(rows * cols * 3);
    const c0 = new Color();
    for (let r = 0; r < rows; r++) {
      const u = r * STEP_U;
      for (let c = 0; c < cols; c++) {
        const v = -HALF + c * STEP_V;
        const i = r * cols + c;
        const h = trackHeight(u, v);
        top[i] = base[i] + h;
        const [e, n] = this.offset(u, v);
        // Where the dirt meets the ground it tucks under it, so the two never fight.
        position.set([e, top[i] - 0.14 * (1 - Math.min(1, h / TRACK_LIFT)), -n], i * 3);
        const onTop = Math.abs(v) < TRACK_WIDTH / 2;
        c0.copy(onTop ? TOP : h > 0.25 ? SIDE : GRASS_EDGE);
        // Packed and rutted: a little variation, darker down the racing line.
        const grain = Math.sin(u * 2.3 + v * 1.7) * 0.04 + Math.sin(u * 0.7 - v * 3.1) * 0.03 - (Math.abs(v) < 1 ? 0.05 : 0);
        c0.offsetHSL(0, 0, grain);
        color.set([c0.r, c0.g, c0.b], i * 3);
      }
    }
    const index: number[] = [];
    for (let r = 0; r < rows - 1; r++) {
      for (let c = 0; c < cols - 1; c++) {
        const a = r * cols + c, b = a + 1, d = a + cols, e = d + 1;
        index.push(a, b, d, b, e, d);
      }
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(position, 3));
    geometry.setAttribute('color', new BufferAttribute(color, 3));
    geometry.setIndex(index);
    geometry.computeVertexNormals();
    const mesh = new Mesh(geometry, dirt);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.group.add(mesh);

    // An orange flag either side of each lip.
    for (const u of LIPS) {
      for (const side of [-1, 1]) {
        const v = side * (TRACK_WIDTH / 2 + 1.2);
        const [e, n] = this.offset(u, v);
        const r = Math.round(u / STEP_U), c = Math.round((v + HALF) / STEP_V);
        const y = top[r * cols + c];
        const stick = new Mesh(new CylinderGeometry(0.025, 0.03, 1.8, 5), pole);
        stick.position.set(e, y + 0.9, -n);
        const cloth = new Mesh(new ConeGeometry(0.22, 0.5, 3).rotateZ(-Math.PI / 2), flag);
        cloth.position.set(e + this.fe * 0.25, y + 1.6, -n - this.fn * 0.25);
        cloth.rotation.y = Math.atan2(-this.fe, -this.fn) + Math.PI / 2;
        for (const m of [stick, cloth]) { m.castShadow = true; this.group.add(m); }
      }
    }
    this.top = top;
    return true;
  }

  /** The line's surface at a coordinate, metres above sea level, or undefined off it. */
  heightAt(lat: number, lon: number): number | undefined {
    const top = this.top;
    if (top === undefined) return undefined;
    const e = (lon - this.lon) * METRES_PER_DEG * Math.cos(this.lat * DEG_TO_RAD);
    const n = (lat - this.lat) * METRES_PER_DEG;
    const u = e * this.fe + n * this.fn;
    const v = e * this.fn - n * this.fe;
    const fr = u / STEP_U, fc = (v + HALF) / STEP_V;
    if (fr < 0 || fc < 0 || fr >= this.rows - 1 || fc >= this.cols - 1) return undefined;
    const r = Math.floor(fr), c = Math.floor(fc), tr = fr - r, tc = fc - c;
    const i = r * this.cols + c;
    const a = top[i] + (top[i + 1] - top[i]) * tc;
    const b = top[i + this.cols] + (top[i + this.cols + 1] - top[i + this.cols]) * tc;
    return a + (b - a) * tr;
  }
}

/**
 * The lines in the world: a few at most, the oldest cleared when a new one
 * is laid. Each is placed in the scene by its anchor every frame.
 */
export class JumpTracks {
  readonly group = new Group();
  private readonly lines: JumpLine[] = [];

  add(line: JumpLine): void {
    this.lines.push(line);
    this.group.add(line.group);
    while (this.lines.length > 3) {
      const old = this.lines.shift()!;
      this.group.remove(old.group);
      old.group.traverse((o) => { if (o instanceof Mesh) o.geometry.dispose(); });
    }
  }

  /** Build what is waiting for its ground, and put every line where its anchor is now. */
  update(ground: Ground, toLocal: (lat: number, lon: number) => { east: number; north: number }): void {
    for (const line of this.lines) {
      if (!line.build(ground)) continue;
      const at = toLocal(line.lat, line.lon);
      line.group.position.set(at.east, 0, -at.north);
    }
  }

  /** The highest line surface at a coordinate, if any. */
  heightAt(lat: number, lon: number): number | undefined {
    let best: number | undefined;
    for (const line of this.lines) {
      const h = line.heightAt(lat, lon);
      if (h !== undefined && (best === undefined || h > best)) best = h;
    }
    return best;
  }
}

/**
 * Which way to lay a line from here: the heading nearest `yaw` whose ground
 * has fewest trees and walls on it, and is not too steep. `blocked` says
 * whether a spot `ahead` metres along and `side` across a heading is solid.
 */
export function clearHeading(yaw: number, blocked: (yaw: number, ahead: number, side: number) => boolean,
  steepness: (yaw: number) => number): number {
  let best = yaw, bestScore = Infinity;
  for (let k = 0; k <= 12; k++) {
    const turn = (k % 2 === 0 ? 1 : -1) * Math.ceil(k / 2) * (Math.PI / 12);
    const candidate = yaw + turn;
    let hits = 0;
    for (let ahead = 10; ahead <= 10 + TRACK_LENGTH; ahead += 5) {
      for (const side of [-4, 0, 4]) if (blocked(candidate, ahead, side)) hits++;
    }
    const score = hits * 10 + steepness(candidate) * 40 + Math.abs(turn) * 2;
    if (score < bestScore) { bestScore = score; best = candidate; }
  }
  return best;
}
